import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/styles";
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight, ContentCopy } from "@mui/icons-material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/theme-monokai";

import { format } from "../../../utils/Date";
import { send_request } from "../../../utils/Request";
import { isLegacyForm } from "../../../utils/formTypeConstants";
import { processApiComponentOutput } from "../../../utils/ApiComponentOutputProcessor";
import { isSessionIoV1RenderingEnabled } from "../../../constants/featureFlags";
import config from "../../../config";
import useProjectStore from "../../ProjectCanvas/useProjectStore";
import {
  SESSION_IO_VIEW_MODES,
  hasIoEnvelope,
  getDisplayStatus,
  getDisplayError,
  getStandardInputData,
  getStandardOutputData,
  getInputBindingsData,
  getExportMapData,
  getDiagnosticsData,
  getRawIoData,
  shouldShowStandardExports,
  getStandardExportSummaryData,
  getV2FormSessionUrl,
} from "./sessionIoViewModel";
import CodeEditorModal from "./CodeEditorModal";
import ComponentWasReRunBox from "../ProjectLog/ComponentWasReRunBox";

// Cap on inline JsonPanel Ace height. With wrapEnabled, this is visual
// lines; content past this point scrolls inside the editor. Modal view
// (CodeEditorModal) renders the full JSON unbounded.
const INLINE_JSON_MAX_LINES = 500;

const TimelineContainer = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  overflow: "hidden",
  marginTop: "12px",
}));

const TimelineHeader = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.background.subtle,
  padding: "8px 12px",
  fontWeight: 500,
  fontSize: "14px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const TimelineBody = styled(Grid)({
  padding: "12px",
});

const InfoText = styled("span")({
  display: "flex",
  alignItems: "center",
  fontSize: "13px",
  "& .MuiSvgIcon-root": {
    fontSize: "14px",
    marginRight: "4px",
    opacity: 0.7,
  },
});

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: "4px",
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    color: theme.palette.text.disabled,
    border: "none",
    padding: "2px 6px",
    fontSize: "11px",
    lineHeight: 1.2,
    textTransform: "none",
    fontWeight: 400,
    minHeight: 0,
    borderRadius: "3px",
    "&.Mui-selected": {
      backgroundColor: "transparent",
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.text.secondary,
    },
    "&.Mui-selected:hover": {
      backgroundColor: "transparent",
    },
  },
}));

const ThreadSelectorContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  gap: "8px",
});

const ThreadLabel = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  fontWeight: 500,
  color: theme.palette.text.secondary,
  display: "flex",
  alignItems: "center",
  "& svg": { fontSize: "16px", marginRight: "4px" },
}));

const ThreadCounter = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
}));

const NavigationButton = styled(IconButton)({
  padding: "2px",
  "&.Mui-disabled": { opacity: 0.3 },
});

const ThreadSelectInput = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    borderRadius: "4px",
    fontSize: "13px",
    height: "32px",
    width: "110px",
  },
  "& .MuiOutlinedInput-input": { padding: "6px 8px" },
  "& .MuiSelect-select": { minHeight: "unset" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider },
}));

const panelIconButtonSx = {
  padding: 0,
  width: "22px",
  height: "22px",
  minWidth: "22px",
  borderRadius: "4px",
  color: "text.secondary",
  "&:hover": {
    backgroundColor: "action.hover",
    color: "text.primary",
  },
  "&:focus-visible": {
    outline: (theme) => `1px solid ${theme.palette.primary.main}`,
    outlineOffset: "-1px",
  },
};

function codeLangToAceEditorType(codeLang) {
  if (codeLang === "NodeJS_16" || codeLang === "javascript")
    return "javascript";
  return "python";
}

function readCodeFromComponent(component) {
  return (
    component?.data?.data?.[".inputData"]?.code ||
    component?.data?.[".inputData"]?.code ||
    component?.data?.data?.code ||
    component?.data?.code ||
    ""
  );
}

function readCodeLangFromComponent(component) {
  return (
    component?.data?.data?.[".inputData"]?.language ||
    component?.data?.[".inputData"]?.language ||
    component?.data?.data?.language ||
    component?.data?.language ||
    component?.data?.data?.[".inputData"]?.codeLang ||
    component?.data?.[".inputData"]?.codeLang ||
    component?.data?.data?.codeLang ||
    component?.data?.codeLang
  );
}

function noInputData(componentData) {
  const noInput = { ...componentData };
  delete noInput[".inputData"];
  return noInput;
}

function isInputString(jsonInput) {
  return typeof jsonInput !== "string";
}

const linkRowIconButtonSx = {
  padding: 0,
  width: "20px",
  height: "20px",
  minWidth: "20px",
  borderRadius: "3px",
  color: "text.secondary",
  flexShrink: 0,
  "&:hover": { backgroundColor: "action.hover", color: "text.primary" },
};

function LinkRow({ label, href, displayText, isLoading, openLabel = "Open" }) {
  const [copied, setCopied] = useState(false);
  const text = displayText || href || "";
  const copy = () => {
    if (!href) return;
    navigator.clipboard.writeText(href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
    >
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 500,
          letterSpacing: 0,
          color: "text.disabled",
          lineHeight: 1,
          flexShrink: 0,
          minWidth: "64px",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          height: "26px",
          px: "8px",
          borderRadius: "4px",
          backgroundColor: "action.hover",
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {isLoading ? (
          <StyledSkeleton
            variant="rectangular"
            height="14px"
            sx={{ flex: 1 }}
          />
        ) : href ? (
          <>
            <Box
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={text}
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: "12px",
                color: "primary.main",
                textDecoration: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {text}
            </Box>
            <Tooltip title={copied ? "Copied!" : "Copy link"}>
              <IconButton
                size="small"
                disableRipple
                onClick={copy}
                sx={linkRowIconButtonSx}
              >
                <ContentCopy sx={{ fontSize: "12px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={openLabel}>
              <IconButton
                size="small"
                disableRipple
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={linkRowIconButtonSx}
              >
                <OpenInNewIcon sx={{ fontSize: "12px" }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
            —
          </Typography>
        )}
      </Box>
    </Box>
  );
}

LinkRow.propTypes = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  displayText: PropTypes.string,
  isLoading: PropTypes.bool,
  openLabel: PropTypes.string,
};

function MetaStrip({ items }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        rowGap: "4px",
        columnGap: "20px",
        alignItems: "center",
      }}
    >
      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            minHeight: "20px",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              letterSpacing: 0,
              color: "text.disabled",
              lineHeight: 1,
            }}
          >
            {item.label}
          </Typography>
          {typeof item.value === "string" || typeof item.value === "number" ? (
            <Typography
              sx={{ fontSize: "12px", color: "text.primary", lineHeight: 1.2 }}
            >
              {item.value}
            </Typography>
          ) : (
            item.value
          )}
        </Box>
      ))}
    </Box>
  );
}

MetaStrip.propTypes = {
  items: PropTypes.array.isRequired,
};

// Provided by SessionDocumentPanel so JsonPanel can repaint Ace search
// markers on keystroke without re-rendering every SessionComponentBody.
export const SearchContext = createContext({
  searchQuery: "",
  searchNavTick: 0,
});

function JsonPanel({
  title,
  data,
  component,
  section,
  aceTheme,
  onOpenInModal,
}) {
  const { searchQuery, searchNavTick } = useContext(SearchContext);
  const [copied, setCopied] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const editorRef = useRef(null);
  const processed = processApiComponentOutput(data, component, section);
  const safe = processed == null ? {} : processed;
  const jsonString =
    typeof safe === "string" ? safe : JSON.stringify(safe, null, 2);

  const copy = () => {
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const markerIdsRef = useRef([]);
  useEffect(() => {
    if (!editorReady) return;
    const editor = editorRef.current;
    if (!editor) return;
    const session = editor.session;
    if (!session) return;

    // Clear previous markers from this panel.
    markerIdsRef.current.forEach((id) => {
      try {
        session.removeMarker(id);
      } catch (_) {}
    });
    markerIdsRef.current = [];

    const q = (searchQuery || "").trim();
    if (!q) return;

    // Pull the Range constructor from the live editor (ace's module loader is
    // unreliable when ace-builds is loaded via ESM — selection always exists).
    let Range;
    try {
      Range = editor.selection.getRange().constructor;
    } catch (_) {
      return;
    }
    if (!Range) return;

    const lines = jsonString.split("\n");
    const ql = q.toLowerCase();
    const ids = [];
    let firstRow = -1;
    for (let row = 0; row < lines.length; row += 1) {
      const lineLower = lines[row].toLowerCase();
      let from = 0;
      while (from < lineLower.length) {
        const at = lineLower.indexOf(ql, from);
        if (at === -1) break;
        try {
          const range = new Range(row, at, row, at + q.length);
          const id = session.addMarker(
            range,
            "wf86-ace-search-hit",
            "text",
            false
          );
          if (id != null) ids.push(id);
          if (firstRow === -1) firstRow = row;
        } catch (_) {}
        from = at + q.length;
      }
    }
    markerIdsRef.current = ids;

    if (firstRow >= 0) {
      try {
        editor.scrollToLine(firstRow, true, false);
      } catch (_) {}
    }
  }, [editorReady, searchQuery, jsonString, searchNavTick]);

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: "4px",
          overflow: "hidden",
          mt: "6px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "32px",
            pl: "10px",
            pr: "6px",
            backgroundColor: (theme) => theme.palette.background.subtle,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "text.secondary",
              lineHeight: 1,
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", gap: "2px" }}>
            <Tooltip title={copied ? "Copied!" : "Copy JSON"}>
              <IconButton
                size="small"
                disableRipple
                onClick={copy}
                sx={panelIconButtonSx}
              >
                <ContentCopy sx={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in full screen">
              <IconButton
                size="small"
                disableRipple
                onClick={() => onOpenInModal(jsonString)}
                sx={panelIconButtonSx}
              >
                <OpenInFullIcon sx={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <AceEditor
          mode="json"
          theme={aceTheme}
          name={`json-panel-${section}-${component?.componentId || "x"}`}
          editorProps={{ $blockScrolling: true }}
          value={jsonString}
          readOnly
          wrapEnabled
          showPrintMargin={false}
          showGutter={false}
          width="100%"
          minLines={2}
          // Bounded so a component with a multi-thousand-line payload
          // doesn't blow up the DOM. Past this height Ace virtualizes
          // its own scroll; "Open in full screen" gives the unbounded
          // viewport via CodeEditorModal.
          maxLines={INLINE_JSON_MAX_LINES}
          onLoad={(editor) => {
            editorRef.current = editor;
            setEditorReady(true);
          }}
          setOptions={{
            useWorker: false,
            foldStyle: "markbegin",
            showFoldWidgets: false,
            highlightActiveLine: false,
            highlightGutterLine: false,
          }}
        />
      </Box>
    </Grid>
  );
}

JsonPanel.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.any,
  component: PropTypes.object,
  section: PropTypes.string.isRequired,
  aceTheme: PropTypes.string.isRequired,
  onOpenInModal: PropTypes.func.isRequired,
};

function ThreadSelector({ component, sessionData, handleAlterThreadPath }) {
  if (!component || component.type !== "loop_through_list") return null;
  if (!sessionData?.components || !Array.isArray(sessionData.components))
    return null;

  const parentThreadId = component.thread?.parentThreadId;
  const successItems = sessionData.components.filter(
    (sd) =>
      sd.type === "loop_through_list" &&
      sd.status === "SUCCESS" &&
      sd.thread?.parentThreadId === parentThreadId
  );

  if (successItems.length <= 1) return null;

  const currentIndex = successItems.findIndex(
    (item) => item.thread.threadId === component.thread.threadId
  );
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < successItems.length - 1;
  const previousThreadId = hasPrevious
    ? successItems[currentIndex - 1].thread.threadId
    : null;
  const nextThreadId = hasNext
    ? successItems[currentIndex + 1].thread.threadId
    : null;

  return (
    <ThreadSelectorContainer>
      <ThreadLabel>
        <AutoAwesomeMotionIcon fontSize="small" /> Threads:
      </ThreadLabel>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <NavigationButton
          size="small"
          disabled={!hasPrevious}
          onClick={() =>
            hasPrevious &&
            handleAlterThreadPath(component.thread.threadId, previousThreadId)
          }
        >
          <ChevronLeft fontSize="small" />
        </NavigationButton>
        <ThreadSelectInput
          select
          variant="outlined"
          size="small"
          value={component.thread.threadId}
          onChange={(e) =>
            handleAlterThreadPath(component.thread.threadId, e.target.value)
          }
        >
          {successItems.map((item, idx) => (
            <MenuItem key={item.thread.threadId} value={item.thread.threadId}>
              Thread {idx + 1}
            </MenuItem>
          ))}
        </ThreadSelectInput>
        <NavigationButton
          size="small"
          disabled={!hasNext}
          onClick={() =>
            hasNext &&
            handleAlterThreadPath(component.thread.threadId, nextThreadId)
          }
        >
          <ChevronRight fontSize="small" />
        </NavigationButton>
      </Box>
      <ThreadCounter>of {successItems.length} threads</ThreadCounter>
    </ThreadSelectorContainer>
  );
}

ThreadSelector.propTypes = {
  component: PropTypes.object,
  sessionData: PropTypes.object,
  handleAlterThreadPath: PropTypes.func.isRequired,
};

function getAiAgentId(component) {
  return (
    component?.io?.input?.resolved?.agentId ||
    component?.io?.output?.resolved?.agentId ||
    component?.data?.agentId ||
    component?.data?.data?.agentId ||
    component?.data?.data?.aiAgentData?.agentId ||
    component?.data?.aiAgentData?.agentId ||
    null
  );
}

function getAiChatSessionId(component) {
  return (
    component?.io?.output?.resolved?.chatSessionId ||
    component?.data?.chatSessionId ||
    component?.data?.data?.chatSessionId ||
    null
  );
}

function toTimestamp(value) {
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function SessionComponentBody({
  component,
  sessionData,
  reRuns,
  setSessionData,
  setIsLatestRun,
  setLoadReRun,
  isTest,
  projectTestId,
  handleAlterThreadPath,
}) {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === "dark";
  const aceTheme = isDark ? "monokai" : "github";
  const userName = useProjectStore.getState().userName || "anonymous";
  const clientId = useProjectStore((s) => s.clientId);
  const ioViewStorageKey = `wf86.sessionInfo.ioViewMode.${userName}`;

  const [ioViewMode, setIoViewMode] = useState(() => {
    try {
      const stored = window.localStorage.getItem(ioViewStorageKey);
      return stored === SESSION_IO_VIEW_MODES.ADVANCED
        ? SESSION_IO_VIEW_MODES.ADVANCED
        : SESSION_IO_VIEW_MODES.STANDARD;
    } catch (_) {
      return SESSION_IO_VIEW_MODES.STANDARD;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(ioViewStorageKey, ioViewMode);
    } catch (_) {}
  }, [ioViewMode, ioViewStorageKey]);

  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [jsonModal, setJsonModal] = useState({ open: false, value: "" });
  const [copiedError, setCopiedError] = useState(false);

  const [resolvedAiSession, setResolvedAiSession] = useState(null);
  const aiLookupRef = useRef(0);

  const aiAgentId = getAiAgentId(component) || resolvedAiSession?.agentId;
  const chatSessionId =
    getAiChatSessionId(component) || resolvedAiSession?.chatSessionId;
  const aiAgentSessionUrl =
    aiAgentId && chatSessionId
      ? `/agents/${aiAgentId}?tab=sessions&sessionId=${chatSessionId}`
      : null;

  useEffect(() => {
    if (component?.type !== "ai_agent") return;
    if (chatSessionId) return;
    const directAgentId = getAiAgentId(component);
    const workflowSessionId = sessionData?.projectSessionId;
    if (!directAgentId || !workflowSessionId) return;

    const now = Date.now();
    if (now - aiLookupRef.current < 2000) return;
    aiLookupRef.current = now;

    send_request(
      `project-service/agent/${directAgentId}/sessions`,
      "",
      {},
      "GET"
    )
      .then((res) => {
        const all = Array.isArray(res?.data) ? res.data : [];
        const matches = all.filter(
          (s) => s?.workflowSessionId === workflowSessionId && s?.chatSessionId
        );
        if (!matches.length) return;
        const componentStartAt = toTimestamp(component?.startAt);
        const selected = [...matches].sort((a, b) => {
          if (componentStartAt) {
            const aDiff = Math.abs(
              toTimestamp(a?.createdAt) - componentStartAt
            );
            const bDiff = Math.abs(
              toTimestamp(b?.createdAt) - componentStartAt
            );
            if (aDiff !== bDiff) return aDiff - bDiff;
          }
          return (
            toTimestamp(b?.updatedAt || b?.createdAt) -
            toTimestamp(a?.updatedAt || a?.createdAt)
          );
        })[0];
        if (!selected?.chatSessionId) return;
        setResolvedAiSession({
          agentId: selected.agentId || directAgentId,
          chatSessionId: selected.chatSessionId,
        });
      })
      .catch((err) => console.log(err));
  }, [component, chatSessionId, sessionData?.projectSessionId]);

  const copyErrorToClipboard = (errorText) => {
    navigator.clipboard.writeText(errorText).then(() => {
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 2000);
    });
  };

  const openJsonInModal = (jsonString) => {
    setJsonModal({ open: true, value: jsonString });
  };

  const formSessionId =
    component?.data?.formSessionId ||
    component?.data?.data?.formSessionId ||
    "";
  const formSessionURL =
    component?.data?.formSessionURL ||
    component?.data?.data?.formSessionURL ||
    "";
  const showLegacyFormSessionLink =
    isLegacyForm(component?.type) &&
    component?.data &&
    (formSessionId || formSessionURL || component?.data?.data);

  const v2FormLink = getV2FormSessionUrl({
    component,
    isTest,
    appUrl: config.APP_URL || window.location.origin,
    sessionComponents: sessionData?.components,
  });

  const displayStatus = getDisplayStatus(component);
  const displayError = getDisplayError(component);
  const useIoPath =
    hasIoEnvelope(component) && isSessionIoV1RenderingEnabled(clientId);

  const docUrl = (() => {
    if (
      component?.status !== "SUCCESS" ||
      component?.type !== "document_editor" ||
      !component?.data
    ) {
      return null;
    }
    const payload = component.data.data ?? component.data;
    const docKey = Object.keys(payload).find((k) => k !== ".inputData");
    return docKey ? payload[docKey] : null;
  })();

  const legacyFormHref = (() => {
    if (!showLegacyFormSessionLink) return null;
    if (formSessionURL && !formSessionId) return formSessionURL;
    const base = config.FORM.FORM_FRONT_END.replace(/\/+$/, "");
    if (!isTest) {
      return `${base}/form/${component.componentId}/${formSessionId}`.replace(
        /([^:]\/)\/+/g,
        "$1"
      );
    }
    const testSeg = component.type !== "form" ? `${projectTestId}/` : "";
    return `${base}/form/form_test/${component.componentId}/${testSeg}${formSessionId}`.replace(
      /([^:]\/)\/+/g,
      "$1"
    );
  })();

  const code = readCodeFromComponent(component);
  const codeLang = readCodeLangFromComponent(component);

  return (
    <Grid container spacing={2}>
        <ThreadSelector
          component={component}
          sessionData={sessionData}
          handleAlterThreadPath={handleAlterThreadPath}
        />

        <Grid item xs={12}>
          <TimelineContainer>
            <TimelineBody container spacing={2}>
              {v2FormLink && (
                <Grid item xs={12}>
                  <LinkRow
                    label="Form"
                    href={v2FormLink}
                    isLoading={component.status === "INPROGRESS"}
                    openLabel="Open form"
                  />
                </Grid>
              )}

              {showLegacyFormSessionLink && (
                <Grid item xs={12}>
                  <LinkRow
                    label="Form"
                    href={legacyFormHref}
                    isLoading={component.status === "INPROGRESS"}
                    openLabel="Open form"
                  />
                </Grid>
              )}

              {docUrl && (
                <Grid item xs={12}>
                  <LinkRow
                    label="Document"
                    href={docUrl}
                    openLabel="Open document"
                  />
                </Grid>
              )}

              {component.type === "ai_agent" && (
                <Grid item xs={12}>
                  <LinkRow
                    label="Chat Session"
                    href={aiAgentSessionUrl}
                    displayText={
                      aiAgentSessionUrl ? "View chat session" : undefined
                    }
                    isLoading={
                      !aiAgentSessionUrl && component.status === "INPROGRESS"
                    }
                    openLabel="Open chat session"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <MetaStrip
                  items={[
                    {
                      label: "Started",
                      value: format(
                        component.startAt,
                        useProjectStore.getState().timezone
                      ),
                    },
                    component.status !== "WAITING" &&
                    component.status !== "PAUSED" &&
                    component.status !== "INPROGRESS"
                      ? {
                          label: "Ended",
                          value: format(
                            component.endAt,
                            useProjectStore.getState().timezone
                          ),
                        }
                      : null,
                    component.retryCount != null && component.retryCount > 0
                      ? { label: "Retries", value: component.retryCount }
                      : null,
                  ].filter(Boolean)}
                />
              </Grid>

              {component.type === "code" && code && (
                <Grid item xs={12}>
                  <Grid container marginBottom={2}>
                    <Grid
                      item
                      xs={12}
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ marginBottom: "8px" }}
                    >
                      <InfoText>Code:</InfoText>
                      <Button
                        startIcon={<OpenInFullIcon />}
                        size="small"
                        onClick={() => setIsCodeModalOpen(true)}
                        sx={{ textTransform: "none", fontSize: "13px" }}
                      >
                        Open
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <AceEditor
                        mode={codeLangToAceEditorType(codeLang)}
                        theme={aceTheme}
                        name={`code-editor-${component.componentId}`}
                        editorProps={{ $blockScrolling: true }}
                        value={code}
                        readOnly
                        width="100%"
                        minLines={2}
                        maxLines={20}
                        wrapEnabled
                      />
                    </Grid>
                  </Grid>
                  <CodeEditorModal
                    open={isCodeModalOpen}
                    onClose={() => setIsCodeModalOpen(false)}
                    code={code}
                    codeLang={codeLang}
                  />
                </Grid>
              )}

              {displayError && (
                <Grid item xs={12}>
                  <Grid container alignItems="flex-start" spacing={1}>
                    <Grid item xs={3}>
                      <InfoText>Error:</InfoText>
                    </Grid>
                    <Grid item xs={9}>
                      <Box sx={{ position: "relative" }}>
                        <Typography
                          className="warning preformatted"
                          sx={{
                            fontSize: "13px",
                            padding: "8px",
                            paddingRight: "40px",
                            backgroundColor: "rgba(176, 0, 32, 0.05)",
                            borderRadius: "4px",
                            border: "1px solid rgba(176, 0, 32, 0.1)",
                          }}
                        >
                          {displayError}
                        </Typography>
                        <Tooltip title={copiedError ? "Copied!" : "Copy error"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => copyErrorToClipboard(displayError)}
                              sx={{
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                padding: "4px",
                                backgroundColor: copiedError
                                  ? "rgba(76, 175, 80, 0.1)"
                                  : "background.paper",
                              }}
                            >
                              <ContentCopy
                                sx={{
                                  fontSize: "16px",
                                  color: copiedError
                                    ? "#4CAF50"
                                    : "text.secondary",
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {hasIoEnvelope(component) &&
                ioViewMode === SESSION_IO_VIEW_MODES.STANDARD &&
                displayError && (
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      onClick={() =>
                        setIoViewMode(SESSION_IO_VIEW_MODES.ADVANCED)
                      }
                      sx={{ textTransform: "none" }}
                    >
                      View advanced details
                    </Button>
                  </Grid>
                )}
            </TimelineBody>
          </TimelineContainer>
        </Grid>

        {useIoPath ? (
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <StyledToggleButtonGroup
                    value={ioViewMode}
                    exclusive
                    onChange={(_, next) => next && setIoViewMode(next)}
                    size="small"
                  >
                    <ToggleButton value={SESSION_IO_VIEW_MODES.STANDARD}>
                      Standard
                    </ToggleButton>
                    <ToggleButton value={SESSION_IO_VIEW_MODES.ADVANCED}>
                      Advanced
                    </ToggleButton>
                  </StyledToggleButtonGroup>
                </Box>
              </Grid>

              <JsonPanel
                title="Inputs"
                data={getStandardInputData(component)}
                component={component}
                section="inputs"
                aceTheme={aceTheme}
                onOpenInModal={openJsonInModal}
              />
              <JsonPanel
                title="Outputs"
                data={getStandardOutputData(component)}
                component={component}
                section="outputs"
                aceTheme={aceTheme}
                onOpenInModal={openJsonInModal}
              />

              {ioViewMode === SESSION_IO_VIEW_MODES.STANDARD &&
                shouldShowStandardExports(component) && (
                  <JsonPanel
                    title="Available for Next Steps"
                    data={getStandardExportSummaryData(component)}
                    component={component}
                    section="exports"
                    aceTheme={aceTheme}
                    onOpenInModal={openJsonInModal}
                  />
                )}

              {ioViewMode === SESSION_IO_VIEW_MODES.ADVANCED && (
                <>
                  <JsonPanel
                    title="Input Bindings"
                    data={getInputBindingsData(component)}
                    component={component}
                    section="inputBindings"
                    aceTheme={aceTheme}
                    onOpenInModal={openJsonInModal}
                  />
                  <JsonPanel
                    title="Export Map"
                    data={getExportMapData(component)}
                    component={component}
                    section="exportMap"
                    aceTheme={aceTheme}
                    onOpenInModal={openJsonInModal}
                  />
                  <JsonPanel
                    title="Diagnostics"
                    data={getDiagnosticsData(component)}
                    component={component}
                    section="diagnostics"
                    aceTheme={aceTheme}
                    onOpenInModal={openJsonInModal}
                  />
                  <JsonPanel
                    title="Raw io.v1 JSON"
                    data={getRawIoData(component)}
                    component={component}
                    section="rawIo"
                    aceTheme={aceTheme}
                    onOpenInModal={openJsonInModal}
                  />
                </>
              )}
            </Grid>
          </Grid>
        ) : isLegacyForm(component.type) ? (
          component.status === "SUCCESS" ? (
            <JsonPanel
              title="Data"
              data={
                component.data && component.data.data
                  ? noInputData(component.data.data)
                  : component.data
                  ? noInputData(component.data)
                  : {}
              }
              component={component}
              section="data"
              aceTheme={aceTheme}
              onOpenInModal={openJsonInModal}
            />
          ) : (
            <JsonPanel
              title="Form Configuration"
              data={
                component.data && component.data.data
                  ? noInputData(component.data.data)
                  : component.data
                  ? noInputData(component.data)
                  : {}
              }
              component={component}
              section="formConfig"
              aceTheme={aceTheme}
              onOpenInModal={openJsonInModal}
            />
          )
        ) : component.status === "SUCCESS" ||
          component.status === "SUCCESS_END" ? (
          <>
            <JsonPanel
              title="Inputs"
              data={
                component.data && component.data.data
                  ? isInputString(component.data.data[".inputData"])
                    ? component.data.data[".inputData"]
                    : { Input: component.data.data[".inputData"] }
                  : component.data
                  ? isInputString(component.data[".inputData"])
                    ? component.data[".inputData"]
                    : { Input: component.data[".inputData"] }
                  : {}
              }
              component={component}
              section="inputs"
              aceTheme={aceTheme}
              onOpenInModal={openJsonInModal}
            />
            <JsonPanel
              title="Outputs"
              data={
                component.data && component.data.data
                  ? noInputData(component.data.data)
                  : component.data
                  ? noInputData(component.data)
                  : {}
              }
              component={component}
              section="outputs"
              aceTheme={aceTheme}
              onOpenInModal={openJsonInModal}
            />
          </>
        ) : (
          <>
            <JsonPanel
              title="Inputs"
              data={component?.data?.data || component?.data || {}}
              component={component}
              section="inputs"
              aceTheme={aceTheme}
              onOpenInModal={openJsonInModal}
            />
            <Grid item xs={12}>
              <TimelineContainer>
                <TimelineHeader item xs={12}>
                  Outputs
                </TimelineHeader>
                <TimelineBody>
                  <Box
                    sx={{
                      padding: "8px",
                      color: "text.disabled",
                      fontSize: "14px",
                    }}
                  >
                    No outputs to display
                  </Box>
                </TimelineBody>
              </TimelineContainer>
            </Grid>
          </>
        )}

        {component.wasReRun && reRuns && (
          <Grid item xs={12} sx={{ mt: 1 }}>
            <ComponentWasReRunBox
              session={sessionData}
              reRuns={reRuns}
              setSession={setSessionData}
              setIsLatestRun={setIsLatestRun}
              setLoadReRun={setLoadReRun}
              calculateTimeElapsed={(item) => item}
            />
          </Grid>
        )}

        <CodeEditorModal
          open={jsonModal.open}
          onClose={() => setJsonModal({ open: false, value: "" })}
          code={jsonModal.value}
          codeLang="json"
        />
      </Grid>
  );
}

SessionComponentBody.propTypes = {
  component: PropTypes.object.isRequired,
  sessionData: PropTypes.object,
  reRuns: PropTypes.array,
  setSessionData: PropTypes.func,
  setIsLatestRun: PropTypes.func,
  setLoadReRun: PropTypes.func,
  isTest: PropTypes.bool,
  projectTestId: PropTypes.string,
  handleAlterThreadPath: PropTypes.func.isRequired,
};

export default SessionComponentBody;
